import * as Dialog from "@radix-ui/react-dialog";
import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import {
  DEFAULT_GROUP_SCHEDULE_SLUG,
  LocationVarInheritance,
  LocationVarKey,
  MemberInterface,
  RoleInterfaceMap,
  RoutingGroupContactRefusalBehavior,
  RoutingGroupEscalationDispatch,
  RoutingGroupEscalationMode,
  RoutingGroupInterface,
  RoutingGroupOutsideHoursMode,
  RoutingGroupPickStrategy,
  RoutingGroupRequiredContactInfo,
  RoutingGroupTicketAction,
  RoutingGroupTicketAssignment,
  STORAGE_ROUTING_TOPICS,
  ScheduleInterface,
  joinExternalReceipts,
  splitExternalReceipts
} from "@swivl-mono/types";
import {
  Button,
  ButtonColor,
  ButtonVariant,
  Checkbox,
  InheritancePill,
  Input,
  Panel,
  PanelPosition,
  PhoneInput,
  Popover,
  PopoverPosition,
  Select,
  SelectSize,
  TextArea,
  Tooltip
} from "@swivl-mono/ui";
import parsePhoneNumberFromString from "libphonenumber-js";
import { motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { BiEditAlt } from "react-icons/bi";
import { FaUserGroup } from "react-icons/fa6";
import { IoClose, IoInformationCircleOutline } from "react-icons/io5";
import { LuChevronDown } from "react-icons/lu";
import { MdLockOutline } from "react-icons/md";

import { SearchableLabel } from "../../../Components/Worksheets/Types/Worksheet.Interface";
import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import { Member } from "../../../Models/Member/Member.Current";
import MembersModel from "../../../Models/Member/Members.Model";
import ModalModel from "../../../Models/Modal/Modal.Model";
import RolesModel from "../../../Models/Roles/Roles.Model";
import { TOPIC_VARS_BY_ID } from "../../../Views/Properties/CortexV3/Topics";
import { CortexTopicWorksheet } from "../Components/CortexTopicWorksheet";
import { FlyoutField, RecipientListEditor, validateEmail } from "../Components/RecipientListEditor";

const DEFAULT_GROUP_SCHEDULE_LABEL = "Call Center Hours";

// Hand-authored — rows carry live handlers, can't be data-driven.
export const SEARCH_LABELS: SearchableLabel[] = [
  { title: "Escalation & Ticketing" },
  { title: "When a request comes in" },
  { title: "How to reach the team" },
  { title: "Who gets the ring" },
  { title: "How to pick the person" },
  { title: "Ring timeout (seconds)" },
  { title: "Max attempts" },
  { title: "Forward phone (in hours)" },
  { title: "Who handles the ticket" },
  { title: "When the team is closed" },
  { title: "Forward phone (outside hours)" },
  { title: "Before connecting a caller to the team" },
  { title: "Before the bot files a ticket, it must have" },
  { title: "If the caller declines to share it" },
  { title: "Hours" },
  { title: "Group Name" },
  { title: "Group Description" },
  { title: "Topics" },
  { title: "Roles" }
];

/**
 * PM Escalation & Ticketing page.
 *
 * Cascading pattern mirrors v2 PM (LocationVar-style): the page resolves
 * one card per routing-group slug at the active scope, picking the
 * narrowest row that applies. Each card is a read-only summary with an
 * "Edit" button that opens a right-side flyout (`GroupEditPanel`).
 *
 *   Inherited card → "Edit" first copies the inherited row's full payload
 *   (settings + topics + roles) down to a new row at the active scope, then
 *   opens the flyout against that now-Custom row.
 *
 *   Custom card → "Edit" opens the flyout directly. The flyout footer offers
 *   a `Revert to inherited` / `Delete group` action; confirming deletes the
 *   current-scope row and the card falls back to whichever broader scope it
 *   came from (or disappears if there is none).
 */
// ENG-3390 — the page also owns the scope-wide "Before connecting a caller to the team" variable.
export const PAGE_KEYS: LocationVarKey[] = TOPIC_VARS_BY_ID["escalation-pushback"] ?? [];

interface ResolvedGroup {
  group: RoutingGroupInterface;
  inheritance: LocationVarInheritance;
  /**
   * Only meaningful for Custom cards (`inheritance === NONE`). True when
   * another non-archived row with the same slug exists at a broader scope
   * — i.e. this Custom card is an override of an inherited group, and
   * "deleting" it should fall back to the broader scope (Revert). False
   * when this Custom card was created from scratch with no broader-scope
   * row to fall back to — "deleting" removes it entirely.
   */
  hasBroaderScopeRow: boolean;
}

const groupScopeRank = (r: RoutingGroupInterface): number => (r.locationId != null ? 2 : r.groupId != null ? 1 : 0);

// Stable display order: any customer-created groups first (alphabetical),
// then the seeded defaults in canonical seed order. Slugs not in this list
// are treated as customer-created.
const DEFAULT_GROUP_ORDER: readonly string[] = ["sales", "service", "emergency"];

/**
 * Derive the routing-group's `slug` from the operator-supplied display
 * name. Lowercase, non-alphanumeric runs collapsed to a single hyphen,
 * leading / trailing hyphens stripped. Examples:
 *   "VIP Save"        → "vip-save"
 *   "Customer Care!"  → "customer-care"
 *   "  Tier 1 ops  "  → "tier-1-ops"
 *
 * The slug is internal — operators never see it. We just need a stable,
 * URL-safe identifier for the row's natural key.
 */
function slugifyGroupName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Plain-English labels for each enum value. Used in the read-only summary
// + read-only display on inherited cards; the editor uses these inline too.
function humanizeTicketAction(v: RoutingGroupTicketAction): string {
  return v === "ticket-only" ? "Just create a ticket" : "Create a ticket and try to reach someone";
}
function humanizeEscalationMode(v: RoutingGroupEscalationMode): string {
  return v === "call-center" ? "Ring members in-app" : "Forward calls to a phone number";
}
function humanizeDispatch(v: RoutingGroupEscalationDispatch): string {
  return v === "ring-everyone" ? "Everyone in the group" : "Just one person";
}
function humanizeTicketAssignment(v: RoutingGroupTicketAssignment): string {
  return v === "open-to-group" ? "Open to the whole group" : "Assign to one person";
}
function humanizePick(v: RoutingGroupPickStrategy): string {
  return v === "round-robin" ? "Round-robin" : "Least busy";
}

/**
 * Best-effort phone-number rendering for the read-only summary.
 *
 * First tries to parse as E.164 directly. If that fails (e.g. legacy bad
 * data like `+119174069134` or `+1114385294206` saved by the old PhoneInput
 * before its double-prefix fix), strips leading `1`s one at a time and
 * retries — recovers the most common form of the double-prefix bug without
 * modifying stored data. Falls back to the raw string only if nothing parses
 * (so genuinely-broken values still appear in the UI for an admin to fix).
 */
function formatPhoneNumber(raw: string): string {
  let candidate = raw;
  for (let i = 0; i < 3; i++) {
    const parsed = parsePhoneNumberFromString(candidate);
    if (parsed && parsed.isValid()) return parsed.formatInternational();
    // Bad-data recovery: PhoneInput double-prefix produces `+11…` / `+111…`.
    // Strip one leading "1" after the "+" and retry.
    if (candidate.startsWith("+11")) candidate = "+" + candidate.slice(2);
    else break;
  }
  return raw;
}

/**
 * Format-validate a phone field's current value.
 */
function validatePhone(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = parsePhoneNumberFromString(trimmed);
  return parsed && parsed.isValid() ? null : "Enter a valid phone number.";
}

// ENG-3089 — bot conduct before filing. Option labels are Chuck's (2026-09-17).
const REQUIRED_CONTACT_OPTIONS: Array<{ label: string; value: RoutingGroupRequiredContactInfo }> = [
  { label: "A way to reach the caller (phone or email)", value: "any-contact" },
  { label: "The caller's name", value: "name" },
  { label: "Name and a way to reach them", value: "name-and-contact" },
  { label: "Nothing", value: "none" }
];
const CONTACT_REFUSAL_OPTIONS: Array<{ label: string; value: RoutingGroupContactRefusalBehavior }> = [
  { label: "File the ticket anyway", value: "file-anyway" },
  { label: "File a callback request only", value: "callback-only" },
  { label: "Do not file; offer other options", value: "do-not-file" }
];
function humanizeRequiredContact(v: RoutingGroupRequiredContactInfo): string {
  return REQUIRED_CONTACT_OPTIONS.find((o) => o.value === v)?.label ?? v;
}
function humanizeContactRefusal(v: RoutingGroupContactRefusalBehavior): string {
  return CONTACT_REFUSAL_OPTIONS.find((o) => o.value === v)?.label ?? v;
}
function humanizeOutsideHours(v: RoutingGroupOutsideHoursMode): string {
  switch (v) {
    case "queue":
      return "Hold for the team";
    case "forward":
      return "Forward calls to a number";
    case "reject":
      return "Decline politely";
  }
}

function humanizeExternalReceipts(recipients: string[]): string {
  const { facility, dm, custom } = splitExternalReceipts(recipients);
  const parts = [facility && "Facility", dm && "DM", custom.length > 0 && `${custom.length} custom`].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "Off";
}

const DAY_ABBREV: readonly string[] = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function formatTimeOfDay(minutes: number): string {
  const normalized = minutes % 1440;
  let hours = Math.floor(normalized / 60);
  const mins = normalized % 60;
  const meridiem = hours >= 12 ? "PM" : "AM";
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return mins === 0 ? `${hours} ${meridiem}` : `${hours}:${String(mins).padStart(2, "0")} ${meridiem}`;
}

/**
 * One-line plain-English summary of a weekly schedule. Consecutive days
 * with the same hours collapse to a range; the rest list individually.
 * Used to display a routing group's effective Hours without rendering
 * the full WeeklyGrid editor (which lives on the Hours page).
 */
function summarizeWeeklyPeriods(weekly: { dayOfWeek: number; openMinute: number; closeMinute: number }[]): string {
  if (!weekly || weekly.length === 0) return "Closed every day";
  const perDay: string[] = [];
  for (let d = 0; d < 7; d++) {
    const periods = weekly.filter((p) => p.dayOfWeek === d).sort((a, b) => a.openMinute - b.openMinute);
    if (periods.length === 0) perDay.push("Closed");
    else perDay.push(periods.map((p) => `${formatTimeOfDay(p.openMinute)} – ${formatTimeOfDay(p.closeMinute)}`).join(", "));
  }
  type Group = { start: number; end: number; text: string };
  const groups: Group[] = [];
  for (let d = 0; d < 7; d++) {
    const last = groups[groups.length - 1];
    if (last && last.text === perDay[d] && last.end === d - 1) {
      last.end = d;
    } else {
      groups.push({ start: d, end: d, text: perDay[d] });
    }
  }
  return groups
    .map((g) => {
      const dayLabel = g.start === g.end ? DAY_ABBREV[g.start] : `${DAY_ABBREV[g.start]}–${DAY_ABBREV[g.end]}`;
      return `${dayLabel} ${g.text}`;
    })
    .join(", ");
}

// Order-insensitive equality for the array-valued settings (topics, roles,
// alert recipients) — operators can reorder without it counting as a change.
function sameSet(a: readonly string[] | null | undefined, b: readonly string[] | null | undefined): boolean {
  const as = [...(a ?? [])].sort();
  const bs = [...(b ?? [])].sort();
  return as.length === bs.length && as.every((v, i) => v === bs[i]);
}

/**
 * True when two routing-group rows carry identical behaviour-defining
 * settings. Used to "auto-heal" the inheritance pill: an override row at a
 * narrow scope that exactly matches the row it inherits from is displayed as
 * inherited (From Org / From Group), not Custom. Nullable scalars are
 * normalized so `null`/`undefined`/empty-string don't read as differences.
 */
function groupSettingsEqual(a: RoutingGroupInterface, b: RoutingGroupInterface): boolean {
  const scalar = (x: string | number | boolean | null | undefined): string | number | boolean | null => x ?? null;
  return (
    scalar(a.name) === scalar(b.name) &&
    scalar(a.description) === scalar(b.description) &&
    scalar(a.isEnabled) === scalar(b.isEnabled) &&
    scalar(a.defaultTicketAction) === scalar(b.defaultTicketAction) &&
    scalar(a.escalationMode) === scalar(b.escalationMode) &&
    scalar(a.escalationCallCenterDispatch) === scalar(b.escalationCallCenterDispatch) &&
    scalar(a.escalationCallCenterPick) === scalar(b.escalationCallCenterPick) &&
    scalar(a.escalationForwardPhoneNumber) === scalar(b.escalationForwardPhoneNumber) &&
    scalar(a.outsideHoursForwardPhoneNumber) === scalar(b.outsideHoursForwardPhoneNumber) &&
    scalar(a.escalationRingTimeoutSeconds) === scalar(b.escalationRingTimeoutSeconds) &&
    scalar(a.escalationMaxAttempts) === scalar(b.escalationMaxAttempts) &&
    scalar(a.ticketAssignment) === scalar(b.ticketAssignment) &&
    scalar(a.ticketAssignmentPick) === scalar(b.ticketAssignmentPick) &&
    scalar(a.scheduleSlug) === scalar(b.scheduleSlug) &&
    scalar(a.outsideHoursMode) === scalar(b.outsideHoursMode) &&
    scalar(a.requiredContactInfo) === scalar(b.requiredContactInfo) &&
    scalar(a.contactRefusalBehavior) === scalar(b.contactRefusalBehavior) &&
    sameSet(a.topics, b.topics) &&
    sameSet(a.roleSlugs, b.roleSlugs) &&
    sameSet(a.alertSmsRecipients, b.alertSmsRecipients) &&
    sameSet(a.alertEmailRecipients, b.alertEmailRecipients)
  );
}

function compareResolvedGroups(a: ResolvedGroup, b: ResolvedGroup): number {
  const aIdx = DEFAULT_GROUP_ORDER.indexOf(a.group.slug);
  const bIdx = DEFAULT_GROUP_ORDER.indexOf(b.group.slug);
  const aIsDefault = aIdx >= 0;
  const bIsDefault = bIdx >= 0;
  if (aIsDefault && !bIsDefault) return 1;
  if (!aIsDefault && bIsDefault) return -1;
  if (aIsDefault && bIsDefault) return aIdx - bIdx;
  return a.group.name.localeCompare(b.group.name);
}

export default function EscalationAndTicketingPage(): React.ReactElement {
  const orgId = Member.orgId!;
  const page = usePropertiesGetLocationIds();
  const activeGroupId: number | null = page.groupId ?? null;
  const activeLocationId: number | null = page.locationId ?? null;

  const [allGroups, setAllGroups] = useState<RoutingGroupInterface[]>([]);
  const [allSchedules, setAllSchedules] = useState<ScheduleInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which group's Edit flyout is open, tracked by slug at the page level so the
  // open state is stable across reloads (the slug is the card's key, so it
  // survives an inherited card being customized down to the active scope).
  const [editingSlug, setEditingSlug] = useState<string | null>(null);

  // Refetch groups + schedules. We deliberately do NOT flip `loading` back on
  // here: blanking the whole page to "Loading…" on every edit-triggered reload
  // unmounts the open Edit flyout, making it close + reopen. The initial
  // `loading` state (true until the first load resolves) is the only time we
  // gate the page; subsequent reloads refresh data in place.
  const reload = async () => {
    const [g, s] = await Promise.all([API.RoutingGroup.listGroups(orgId), API.Schedule.listAllSchedulesForOrg(orgId)]);
    setAllGroups(g);
    setAllSchedules(s);
    setLoading(false);
  };

  useEffect(() => {
    void reload();
    void RolesModel.loadRoles(orgId);
    void MembersModel.loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // The location's parent group, when viewing at location scope. Lets us
  // include the parent group's schedule rows in the reachable set.
  const activeScopeParentGroupId: number | null = activeLocationId != null ? (page.location?.groupId ?? null) : activeGroupId;

  // Schedules that are visible (inheritable) at the active scope. Drives the
  // slug picker on each group card.
  //   - Org-scope rows: always reachable
  //   - Group-scope rows: reachable when the active scope sits inside that group
  //   - Location-scope rows: reachable only at that exact location
  const reachableSchedules = useMemo<ScheduleInterface[]>(() => {
    return allSchedules.filter((s) => {
      if (!s.isActive) return false;
      if (s.groupId == null && s.locationId == null) return true;
      if (s.groupId != null && s.locationId == null) return s.groupId === activeScopeParentGroupId;
      if (s.locationId != null) return s.locationId === activeLocationId;
      return false;
    });
  }, [allSchedules, activeScopeParentGroupId, activeLocationId]);

  // Distinct slugs available at this scope, with a stable label per slug
  // (the most-specific row's `label` wins, since that's what an operator
  // is most likely recognizing). The default routing slug is always
  // offered even if no row exists — picks fall through to the cascade.
  const reachableSlugOptions = useMemo<Array<{ slug: string; label: string }>>(() => {
    const labelBySlug = new Map<string, string>();
    const rankBySlug = new Map<string, number>();
    for (const s of reachableSchedules) {
      const rank = s.locationId != null ? 3 : s.groupId != null ? 2 : 1;
      const prevRank = rankBySlug.get(s.slug) ?? 0;
      if (rank > prevRank) {
        labelBySlug.set(s.slug, s.label || s.slug);
        rankBySlug.set(s.slug, rank);
      }
    }
    const out: Array<{ slug: string; label: string }> = [];
    for (const [slug, label] of labelBySlug) out.push({ slug, label });
    if (!labelBySlug.has(DEFAULT_GROUP_SCHEDULE_SLUG)) {
      out.unshift({ slug: DEFAULT_GROUP_SCHEDULE_SLUG, label: DEFAULT_GROUP_SCHEDULE_LABEL });
    }
    return out.sort((a, b) => a.label.localeCompare(b.label));
  }, [reachableSchedules]);

  // Cascade-resolve a slug → narrowest reachable row at the active scope, or
  // null when no schedule has been configured for that slug at any scope.
  const resolveScheduleBySlug = useMemo(() => {
    return (slug: string): ScheduleInterface | null => {
      const matches = reachableSchedules.filter((s) => s.slug === slug);
      if (matches.length === 0) return null;
      return matches.reduce((best, cur) => {
        const rank = (s: ScheduleInterface) => (s.locationId != null ? 3 : s.groupId != null ? 2 : 1);
        return rank(cur) > rank(best) ? cur : best;
      });
    };
  }, [reachableSchedules]);

  // Merge all three role sources so chip lookups by slug resolve for the
  // TS-injected defaults (admin/agent/manager live in `defaultRoles`,
  // parent-org customs in `parentRoles`, this org's customs in `roles`).
  const rolesState = RolesModel.useState();
  const roles = useMemo(
    () => ({ ...(rolesState.defaultRoles ?? {}), ...(rolesState.parentRoles ?? {}), ...(rolesState.roles ?? {}) }),
    [rolesState.defaultRoles, rolesState.parentRoles, rolesState.roles]
  );

  // For each unique slug, pick the narrowest non-archived row that applies at the active scope.
  const resolved = useMemo<ResolvedGroup[]>(() => {
    const bySlug = new Map<string, ResolvedGroup>();
    for (const g of allGroups) {
      if (g.isArchived) continue;
      // Emergency lives on its own tab (PM v3 "Emergency"). Filter it
      // out of the generic E&T list so the two surfaces stay decoupled.
      if (g.slug === "emergency") continue;
      const applies =
        // org-scope rows always apply
        (g.groupId == null && g.locationId == null) ||
        // group-scope rows apply when viewing that group or a location within it
        (g.groupId != null && g.locationId == null && g.groupId === activeGroupId) ||
        // location-scope rows apply only at that exact location
        (g.locationId != null && g.locationId === activeLocationId);
      if (!applies) continue;

      const inheritance: LocationVarInheritance =
        g.locationId != null && g.locationId === activeLocationId
          ? LocationVarInheritance.NONE
          : g.groupId != null && g.locationId == null && g.groupId === activeGroupId && activeLocationId == null
            ? LocationVarInheritance.NONE
            : g.groupId == null && g.locationId == null && activeGroupId == null && activeLocationId == null
              ? LocationVarInheritance.NONE
              : g.groupId != null && g.locationId == null
                ? LocationVarInheritance.GROUP
                : LocationVarInheritance.ORG;

      const prev = bySlug.get(g.slug);
      if (!prev || groupScopeRank(g) > groupScopeRank(prev.group)) {
        bySlug.set(g.slug, { group: g, inheritance, hasBroaderScopeRow: false });
      }
    }
    // Second pass: for each winning Custom row, find the narrowest broader-scope
    // row (same slug) that also applies here — the row this one overrides.
    //   - Presence drives the Revert-vs-Delete affordance.
    //   - If the override is byte-identical to that parent, it's not a real
    //     customization (e.g. a stray copy left by the old customize-on-open
    //     flow). Display it as inherited (From Org / From Group) so the pill is
    //     accurate; a genuine difference keeps it Custom.
    for (const entry of bySlug.values()) {
      if (entry.inheritance !== LocationVarInheritance.NONE) continue;
      const winnerRank = groupScopeRank(entry.group);
      const broaderRows = allGroups.filter(
        (other) =>
          !other.isArchived &&
          other.slug === entry.group.slug &&
          other.id !== entry.group.id &&
          groupScopeRank(other) < winnerRank &&
          // applies at the active scope: org rows always; group rows when in this group
          ((other.groupId == null && other.locationId == null) ||
            (other.groupId != null && other.locationId == null && other.groupId === activeGroupId))
      );
      entry.hasBroaderScopeRow = broaderRows.length > 0;
      if (broaderRows.length === 0) continue;
      const parent = broaderRows.reduce((best, cur) => (groupScopeRank(cur) > groupScopeRank(best) ? cur : best));
      if (groupSettingsEqual(entry.group, parent)) {
        entry.inheritance = parent.groupId != null ? LocationVarInheritance.GROUP : LocationVarInheritance.ORG;
      }
    }
    return Array.from(bySlug.values()).sort(compareResolvedGroups);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allGroups, activeGroupId, activeLocationId]);

  // Per-topic "primary owner" at the active scope. Multi-claim is allowed
  // — multiple groups (custom + inherited) can list the same topic — but
  // exactly one wins routing for the topic at this scope. Custom claims
  // beat inherited claims; within the same tier, most-recently-updated
  // wins (matches the server resolver in ResolveRules.Service). Non-
  // primary claimants render struck through so operators see the
  // override.
  const topicPrimaryAtScope = useMemo(() => {
    const claimantsByTopic: Record<string, ResolvedGroup[]> = {};
    for (const r of resolved) {
      for (const t of r.group.topics ?? []) (claimantsByTopic[t] ??= []).push(r);
    }
    const primary: Record<string, { slug: string; name: string }> = {};
    for (const [t, rs] of Object.entries(claimantsByTopic)) {
      const customs = rs.filter((r) => r.inheritance === LocationVarInheritance.NONE);
      const candidates = customs.length > 0 ? customs : rs;
      const winner = candidates.reduce((best, cur) => {
        const bT = best.group.updatedAt ? new Date(best.group.updatedAt).getTime() : 0;
        const cT = cur.group.updatedAt ? new Date(cur.group.updatedAt).getTime() : 0;
        return cT > bT ? cur : best;
      });
      primary[t] = { slug: winner.group.slug, name: winner.group.name };
    }
    return primary;
  }, [resolved]);

  if (loading) return <EscalationPageSkeleton />;

  // Build the URL to the sibling Emergency tab — same scope, different tab.
  // Pattern matches the rest of PM v3: /properties-v3/[group/<id>][/location/<id>]/page/details-v3/<tab>.
  const emergencyTabUrl = `/properties-v3${activeGroupId != null ? `/group/${activeGroupId}` : ""}${
    activeLocationId != null ? `/location/${activeLocationId}` : ""
  }/page/details-v3/emergency`;

  return (
    // Same container as CortexTopicWorksheet, so the cards line up with the
    // other Details v3 pages. Keep the max-width in sync with it.
    <div className="mx-auto max-w-[960px] space-y-6 p-6">
      <header className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="h-3 w-1 shrink-0 bg-grey-900" />
            <h2 className="text-base font-semibold text-grey-900">Escalation &amp; Ticketing</h2>
          </div>
          <p className="pl-3 text-base text-grey-600">
            The teams that handle escalations and the topics they own. Defaults come from your group; click any group to customize it here.{" "}
            <a className="font-medium text-orange-600 hover:underline" href={emergencyTabUrl}>
              Emergency routing
            </a>{" "}
            lives on its own tab.
          </p>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>
        <Button
          color={ButtonColor.BLACK}
          disabled={creating}
          onClick={() => {
            const existingGroups = resolved.map((r) => ({
              slug: r.group.slug,
              name: r.group.name,
              isCustom: r.inheritance === LocationVarInheritance.NONE
            }));
            // The modal builds the full upsert body (via settingsToBody); the
            // page just stamps the scope + slug + isEnabled and persists.
            const onCreate = async (body: Partial<RoutingGroupInterface>): Promise<void> => {
              setCreating(true);
              setError(null);
              try {
                await API.RoutingGroup.upsertGroup(orgId, {
                  slug: slugifyGroupName(body.name ?? ""),
                  isEnabled: true,
                  ...body,
                  groupId: activeLocationId != null ? null : activeGroupId,
                  locationId: activeLocationId
                });
                await reload();
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              } finally {
                setCreating(false);
              }
            };
            // Bare mode (useDefaultUI=false): the modal renders its own frame.
            ModalModel.showModalWithContent(
              undefined,
              <CreateGroupModal
                existingGroups={existingGroups}
                topicPrimaryAtScope={topicPrimaryAtScope}
                roles={roles}
                slugOptions={reachableSlugOptions}
                resolveScheduleBySlug={resolveScheduleBySlug}
                onCreate={onCreate}
              />,
              undefined,
              undefined,
              true,
              false
            );
          }}
        >
          Create Group
        </Button>
      </header>

      {/* ENG-3390 (Chuck, 2026-09-29): "Before connecting a caller to the team" is one scope-wide
          setting, not a per-group field — the bot cannot know which group a request belongs to
          until it has already decided whether to connect. It lives here, above the groups, where
          operators already look for escalation behaviour; same inheritance + save path as every
          other Cortex variable (CortexTopicWorksheet). */}
      <CortexTopicWorksheet topicId="escalation-pushback" embedded sectionEditing />

      <section>
        {resolved.length === 0 ? (
          <p className="rounded border border-dashed border-grey-200 bg-grey-50 px-3 py-4 text-sm text-grey-500">No groups yet — create one above.</p>
        ) : (
          <div className="space-y-3">
            {resolved.map((r) => (
              <GroupCard
                key={r.group.slug}
                resolved={r}
                orgId={orgId}
                activeGroupId={activeGroupId}
                activeLocationId={activeLocationId}
                topicPrimaryAtScope={topicPrimaryAtScope}
                roles={roles}
                slugOptions={reachableSlugOptions}
                resolveScheduleBySlug={resolveScheduleBySlug}
                isEditing={editingSlug === r.group.slug}
                onEdit={() => setEditingSlug(r.group.slug)}
                onCloseEdit={() => setEditingSlug(null)}
                onChanged={reload}
                onError={setError}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ─── Group card ────────────────────────────────────────────────────────────

interface GroupCardProps {
  resolved: ResolvedGroup;
  orgId: number;
  activeGroupId: number | null;
  activeLocationId: number | null;
  topicPrimaryAtScope: Record<string, { slug: string; name: string }>;
  roles: RoleInterfaceMap;
  slugOptions: Array<{ slug: string; label: string }>;
  resolveScheduleBySlug: (slug: string) => ScheduleInterface | null;
  isEditing: boolean;
  onEdit: () => void;
  onCloseEdit: () => void;
  onChanged: () => Promise<void>;
  onError: (msg: string | null) => void;
}

function GroupCard(props: GroupCardProps) {
  const {
    resolved,
    orgId,
    activeGroupId,
    activeLocationId,
    topicPrimaryAtScope,
    roles,
    slugOptions,
    resolveScheduleBySlug,
    isEditing,
    onEdit,
    onCloseEdit,
    onChanged,
    onError
  } = props;
  const { group, inheritance } = resolved;
  const isCustom = inheritance === LocationVarInheritance.NONE;
  const hasBroaderScopeRow = resolved.hasBroaderScopeRow;
  // Revert/Delete is offered on Custom rows, except the seeded org-scope
  // defaults (no broader scope to fall back to, and we don't want them deleted).
  const canDeleteOrRevert = isCustom && !(!hasBroaderScopeRow && DEFAULT_GROUP_ORDER.includes(group.slug));
  const [busy, setBusy] = useState(false);

  // Persist the buffered flyout form. Upserts the FULL payload at the active
  // scope, so a single call both creates a Custom row (when the card was
  // inherited) and updates an existing Custom row. Nothing is written until
  // the flyout's Save calls this — the card reflects edits only afterward.
  const saveGroup = async (body: Partial<RoutingGroupInterface>) => {
    onError(null);
    try {
      await API.RoutingGroup.upsertGroup(orgId, {
        slug: group.slug,
        isEnabled: group.isEnabled,
        ...body,
        groupId: activeLocationId != null ? null : activeGroupId,
        locationId: activeLocationId
      });
      await onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    }
  };

  // Revert (override → fall back to broader scope) or Delete (no broader scope).
  // Only reachable for Custom cards (a row must exist at this scope).
  const deleteOrRevert = async () => {
    if (!isCustom || busy) return;
    const message = resolved.hasBroaderScopeRow
      ? `Revert customizations to '${group.name}'? This deletes the override at this scope; you'll see the inherited version again.`
      : `Delete '${group.name}'? This removes the group entirely. Topics it owns at this scope will fall back to whatever owns them at a broader scope (if anything).`;
    if (!confirm(message)) return;
    setBusy(true);
    onError(null);
    try {
      await API.RoutingGroup.deleteGroup(orgId, group.id);
      await onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const topicsText =
    (group.topics ?? []).length > 0 ? (group.topics ?? []).map((id) => STORAGE_ROUTING_TOPICS.find((t) => t.id === id)?.label ?? id).join(", ") : "—";
  const rolesText = (group.roleSlugs ?? []).length > 0 ? (group.roleSlugs ?? []).map((slug) => roles[slug]?.name ?? slug).join(", ") : "—";

  return (
    <>
      <div className="flex flex-col gap-4 rounded-lg border border-grey-200 bg-white p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-base font-semibold text-grey-900">{group.name}</p>
              <InheritancePill
                inheritance={inheritance}
                isEditing={false}
                canRevert={false}
                alwaysShow
                customText={{ custom: "Customized" }}
                onClick={() => {}}
              />
            </div>
            {group.description && <p className="text-sm text-grey-600">{group.description}</p>}
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              onClick={onEdit}
              className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-grey-900 hover:text-orange-500"
            >
              Edit
              <BiEditAlt className="size-4" />
            </button>
          </div>
        </div>

        <div className="flex gap-4 text-sm">
          <div className="flex-1">
            <p className="font-semibold text-grey-600">Topics</p>
            <p className="text-grey-900">{topicsText}</p>
          </div>
          <div className="flex-1">
            <p className="font-semibold text-grey-600">Roles</p>
            <p className="text-grey-900">{rolesText}</p>
          </div>
        </div>

        <div className="border-t border-grey-300" />

        <dl className="flex flex-col gap-2 text-sm">
          <SettingRow label="When a request comes in" value={humanizeTicketAction(group.defaultTicketAction)} />
          {group.defaultTicketAction === "ticket-and-human" && (
            <>
              <SettingRow label="How to reach the team" value={humanizeEscalationMode(group.escalationMode)} />
              {group.escalationMode === "call-center" && group.escalationCallCenterDispatch && (
                <SettingRow
                  label="Who gets the ring"
                  value={
                    humanizeDispatch(group.escalationCallCenterDispatch) +
                    (group.escalationCallCenterDispatch === "ring-one" && group.escalationCallCenterPick
                      ? ` (${humanizePick(group.escalationCallCenterPick)})`
                      : "")
                  }
                />
              )}
              {group.escalationMode === "call-forward" && group.escalationForwardPhoneNumber && (
                <SettingRow label="Forward phone (in hours)" value={formatPhoneNumber(group.escalationForwardPhoneNumber)} />
              )}
            </>
          )}
          <SettingRow
            label="Who handles the ticket"
            value={
              humanizeTicketAssignment(group.ticketAssignment) +
              (group.ticketAssignment === "assign-to-one" && group.ticketAssignmentPick ? ` (${humanizePick(group.ticketAssignmentPick)})` : "")
            }
          />
          {group.slug !== "emergency" && (
            <>
              <SettingRow label="Before the bot files a ticket, it must have" value={humanizeRequiredContact(group.requiredContactInfo)} />
              {group.requiredContactInfo !== "none" && (
                <SettingRow label="If the caller declines to share it" value={humanizeContactRefusal(group.contactRefusalBehavior)} />
              )}
            </>
          )}
          <SettingRow label="When the team is closed" value={humanizeOutsideHours(group.outsideHoursMode)} />
          {group.outsideHoursMode === "forward" && (
            <SettingRow
              label="Forward phone (outside hours)"
              value={
                group.outsideHoursForwardPhoneNumber
                  ? formatPhoneNumber(group.outsideHoursForwardPhoneNumber)
                  : group.escalationForwardPhoneNumber
                    ? `${formatPhoneNumber(group.escalationForwardPhoneNumber)} (using in-hours number)`
                    : "(not set)"
              }
            />
          )}
          <div className="flex gap-6">
            <dt className="w-[149px] shrink-0 font-semibold text-grey-600">Hours</dt>
            <dd>
              {(() => {
                const slug = group.scheduleSlug ?? DEFAULT_GROUP_SCHEDULE_SLUG;
                const name = slugOptions.find((o) => o.slug === slug)?.label ?? DEFAULT_GROUP_SCHEDULE_LABEL;
                const sched = resolveScheduleBySlug(slug);
                return (
                  <>
                    <p className="text-grey-900">{name}</p>
                    <p className="text-grey-600">{sched ? summarizeWeeklyPeriods(sched.weekly) : "no schedule configured (always open)"}</p>
                  </>
                );
              })()}
            </dd>
          </div>
          <SettingRow label="External receipts" value={humanizeExternalReceipts(group.alertEmailRecipients ?? [])} />
        </dl>
      </div>

      <Panel
        isOpen={isEditing}
        onToggle={onCloseEdit}
        position={PanelPosition.RIGHT}
        width="480px"
        title={`Edit ${group.name} Group`}
        // Quicker, smoother slide-in than the Panel default (0.3s, eased both ways).
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        // Select / Tooltip render in portals outside the panel DOM; without
        // this, clicking a dropdown option reads as an outside click and
        // closes the whole flyout.
        shouldIgnoreOutsideClick={(e) =>
          e.target instanceof Element && !!e.target.closest("[data-radix-popper-content-wrapper],[data-floating-ui-portal]")
        }
      >
        {isEditing && (
          <GroupEditPanel
            group={group}
            inheritance={inheritance}
            roles={roles}
            topicPrimaryAtScope={topicPrimaryAtScope}
            slugOptions={slugOptions}
            resolveScheduleBySlug={resolveScheduleBySlug}
            canDeleteOrRevert={canDeleteOrRevert}
            hasBroaderScopeRow={hasBroaderScopeRow}
            onSave={async (body) => {
              await saveGroup(body);
              onCloseEdit();
            }}
            onCancel={onCloseEdit}
            onRevert={async () => {
              await deleteOrRevert();
              onCloseEdit();
            }}
          />
        )}
      </Panel>
    </>
  );
}

function SettingRow(props: { label: string; value: string }) {
  return (
    <div className="flex gap-6">
      <dt className="w-[149px] shrink-0 font-semibold text-grey-600">{props.label}</dt>
      <dd className="text-grey-900">{props.value}</dd>
    </div>
  );
}

/**
 * Pulsing placeholder shown while routing groups load. Mirrors the page header
 * plus a few group cards so content doesn't shift in on load.
 */
function EscalationPageSkeleton() {
  return (
    // Same container as the loaded page, so the placeholders sit where the real
    // cards will and nothing jumps when the data arrives.
    <div className="mx-auto max-w-[960px] animate-pulse space-y-6 p-6" aria-hidden>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 space-y-2">
          <div className="h-5 w-56 rounded bg-grey-100" />
          <div className="h-4 w-full max-w-xl rounded bg-grey-100" />
        </div>
        <div className="h-8 w-28 rounded bg-grey-100" />
      </div>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="space-y-4 rounded-lg border border-grey-200 bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="h-5 w-40 rounded bg-grey-100" />
            <div className="h-6 w-16 rounded bg-grey-100" />
          </div>
          <div className="border-t border-grey-200" />
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, j) => (
              <div key={j} className="flex gap-6">
                <div className="h-4 w-[149px] rounded bg-grey-100" />
                <div className="h-4 flex-1 rounded bg-grey-100" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Shared group settings form ─────────────────────────────────────────────

/** All operator-editable fields of a routing group, in UI-friendly form. */
interface GroupSettings {
  name: string;
  description: string;
  topics: string[];
  roleSlugs: string[];
  defaultTicketAction: RoutingGroupTicketAction;
  escalationMode: RoutingGroupEscalationMode;
  escalationCallCenterDispatch: RoutingGroupEscalationDispatch | null;
  escalationCallCenterPick: RoutingGroupPickStrategy | null;
  escalationForwardPhoneNumber: string;
  outsideHoursForwardPhoneNumber: string;
  escalationRingTimeoutSeconds: number | null;
  escalationMaxAttempts: number | null;
  ticketAssignment: RoutingGroupTicketAssignment;
  ticketAssignmentPick: RoutingGroupPickStrategy | null;
  outsideHoursMode: RoutingGroupOutsideHoursMode;
  scheduleSlug: string | null;
  alertEmailRecipients: string[];
  requiredContactInfo: RoutingGroupRequiredContactInfo;
  contactRefusalBehavior: RoutingGroupContactRefusalBehavior;
}

function groupToSettings(g: RoutingGroupInterface): GroupSettings {
  return {
    name: g.name,
    description: g.description ?? "",
    topics: g.topics ?? [],
    roleSlugs: g.roleSlugs ?? [],
    defaultTicketAction: g.defaultTicketAction,
    escalationMode: g.escalationMode,
    escalationCallCenterDispatch: g.escalationCallCenterDispatch,
    escalationCallCenterPick: g.escalationCallCenterPick,
    escalationForwardPhoneNumber: g.escalationForwardPhoneNumber ?? "",
    outsideHoursForwardPhoneNumber: g.outsideHoursForwardPhoneNumber ?? "",
    escalationRingTimeoutSeconds: g.escalationRingTimeoutSeconds,
    escalationMaxAttempts: g.escalationMaxAttempts,
    ticketAssignment: g.ticketAssignment,
    ticketAssignmentPick: g.ticketAssignmentPick,
    outsideHoursMode: g.outsideHoursMode,
    scheduleSlug: g.scheduleSlug,
    alertEmailRecipients: g.alertEmailRecipients ?? [],
    requiredContactInfo: g.requiredContactInfo ?? "any-contact",
    contactRefusalBehavior: g.contactRefusalBehavior ?? "file-anyway"
  };
}

function blankGroupSettings(): GroupSettings {
  return {
    name: "",
    description: "",
    topics: [],
    roleSlugs: [],
    defaultTicketAction: "ticket-and-human",
    escalationMode: "call-center",
    escalationCallCenterDispatch: "ring-one",
    escalationCallCenterPick: "round-robin",
    escalationForwardPhoneNumber: "",
    outsideHoursForwardPhoneNumber: "",
    escalationRingTimeoutSeconds: null,
    escalationMaxAttempts: null,
    ticketAssignment: "open-to-group",
    ticketAssignmentPick: null,
    outsideHoursMode: "queue",
    scheduleSlug: null,
    alertEmailRecipients: [],
    requiredContactInfo: "any-contact",
    contactRefusalBehavior: "file-anyway"
  };
}

/** Convert the buffered form into the upsert body, applying the same
 * mode-dependent nulling for both the Edit flyout and Create modal. */
function settingsToBody(s: GroupSettings): Partial<RoutingGroupInterface> {
  return {
    name: s.name,
    description: s.description,
    topics: s.topics,
    roleSlugs: s.roleSlugs,
    defaultTicketAction: s.defaultTicketAction,
    escalationMode: s.escalationMode,
    escalationCallCenterDispatch: s.escalationMode === "call-center" ? (s.escalationCallCenterDispatch ?? "ring-one") : null,
    escalationCallCenterPick:
      s.escalationMode === "call-center" && (s.escalationCallCenterDispatch ?? "ring-one") === "ring-one"
        ? (s.escalationCallCenterPick ?? "round-robin")
        : null,
    escalationForwardPhoneNumber: s.escalationForwardPhoneNumber.trim() || null,
    outsideHoursForwardPhoneNumber: s.outsideHoursForwardPhoneNumber.trim() || null,
    escalationRingTimeoutSeconds: s.escalationMode === "call-center" ? s.escalationRingTimeoutSeconds : null,
    escalationMaxAttempts: s.escalationMode === "call-center" ? s.escalationMaxAttempts : null,
    ticketAssignment: s.ticketAssignment,
    ticketAssignmentPick: s.ticketAssignment === "assign-to-one" ? (s.ticketAssignmentPick ?? "round-robin") : null,
    outsideHoursMode: s.outsideHoursMode,
    scheduleSlug: s.scheduleSlug,
    alertEmailRecipients: s.alertEmailRecipients,
    requiredContactInfo: s.requiredContactInfo,
    contactRefusalBehavior: s.contactRefusalBehavior
  };
}

interface GroupSettingsFormProps {
  value: GroupSettings;
  onChange: (next: GroupSettings) => void;
  roles: RoleInterfaceMap;
  topicPrimaryAtScope: Record<string, { slug: string; name: string }>;
  slugOptions: Array<{ slug: string; label: string }>;
  resolveScheduleBySlug: (slug: string) => ScheduleInterface | null;
  /** Own slug, for the topic-ownership comparison. Undefined when creating. */
  groupSlug?: string;
  /** Own display name, for the "Claimed by … Group" topic tooltip. Undefined when creating. */
  groupName?: string;
}

/**
 * The full set of group settings fields, shared by the Edit flyout and the
 * Create modal so the two stay identical. Fully controlled: the parent owns
 * the `GroupSettings` state and persists `settingsToBody(value)` on save.
 */
function memberDisplayName(m: MemberInterface): string {
  const full = `${m.account?.firstName ?? ""} ${m.account?.lastName ?? ""}`.trim();
  return full || m.agentName || m.account?.email || `Member #${m.id}`;
}

/** Re-seeds defaultValue from live state on each remount. */
function ForwardPhoneField(props: { value: string; onChange: (v: string) => void; error: string | null }): React.ReactElement {
  const [initial] = useState(() => props.value);
  return (
    <>
      <PhoneInput defaultValue={initial} onChange={props.onChange} />
      {props.error && <p className="text-sm text-red-500">{props.error}</p>}
    </>
  );
}

function GroupSettingsForm(props: GroupSettingsFormProps) {
  const { value: s, onChange, roles, topicPrimaryAtScope, slugOptions, resolveScheduleBySlug, groupSlug, groupName } = props;
  const set = <K extends keyof GroupSettings>(key: K, val: GroupSettings[K]): void => onChange({ ...s, [key]: val });

  const forwardError = validatePhone(s.escalationForwardPhoneNumber);
  const outsideHoursError = validatePhone(s.outsideHoursForwardPhoneNumber);

  // Member names per role slug, for the hover dropdown on each role chip.
  const { members } = MembersModel.useState();
  const [openRoleSlug, setOpenRoleSlug] = useState<string | null>(null);
  const membersByRole = useMemo<Record<string, string[]>>(() => {
    const map: Record<string, string[]> = {};
    for (const m of members ?? []) {
      const name = memberDisplayName(m);
      const seen = new Set<string>();
      for (const p of m.permissions ?? []) {
        if (!p.roleSlug || seen.has(p.roleSlug)) continue;
        seen.add(p.roleSlug);
        (map[p.roleSlug] ??= []).push(name);
      }
    }
    return map;
  }, [members]);

  const resolvedSchedule = resolveScheduleBySlug(s.scheduleSlug ?? DEFAULT_GROUP_SCHEDULE_SLUG);
  const availableRoleOptions = Object.values(roles)
    .filter((r) => !s.roleSlugs.includes(r.slug))
    .map((r) => ({ label: r.name, value: r.slug }));

  return (
    <div className="flex flex-col gap-4">
      <FlyoutField label="Group Name">
        <Input value={s.name} placeholder="e.g. VIP Save" onChange={(e) => set("name", e.target.value)} />
      </FlyoutField>

      <FlyoutField label="Group Description">
        <TextArea rows={3} value={s.description} onChange={(e) => set("description", e.target.value)} />
      </FlyoutField>

      <FlyoutField label="Topics">
        <div className="flex flex-wrap gap-2">
          {STORAGE_ROUTING_TOPICS.filter((t) => t.id !== "emergency").map((t) => {
            const mine = s.topics.includes(t.id);
            const primaryOwner = topicPrimaryAtScope[t.id];
            const lockedByOther = !mine && !!primaryOwner && primaryOwner.slug !== groupSlug;
            const tooltip = mine
              ? groupName
                ? `Claimed by ${groupName} Group`
                : undefined
              : lockedByOther && primaryOwner
                ? `Owned by ${primaryOwner.name}`
                : undefined;
            return (
              <Tooltip key={t.id} content={tooltip} disabled={!tooltip} tooltipOffset={4}>
                <button
                  type="button"
                  disabled={lockedByOther}
                  onClick={() => set("topics", s.topics.includes(t.id) ? s.topics.filter((x) => x !== t.id) : [...s.topics, t.id])}
                  className={filterClasses(
                    "flex h-8 items-center gap-1 rounded border px-2 text-sm",
                    mine
                      ? "cursor-pointer border-teal-900 bg-teal-900 text-white hover:border-teal-800 hover:bg-teal-800"
                      : lockedByOther
                        ? "cursor-not-allowed border-grey-100 bg-grey-100 text-grey-600"
                        : "cursor-pointer border-teal-900 bg-white text-grey-900 hover:bg-teal-50"
                  )}
                >
                  {lockedByOther && <MdLockOutline className="size-3" />}
                  {t.label}
                </button>
              </Tooltip>
            );
          })}
        </div>
      </FlyoutField>

      <FlyoutField label="Roles">
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {s.roleSlugs.map((slug) => {
              const role = roles[slug];
              const memberNames = membersByRole[slug] ?? [];
              return (
                <span
                  key={slug}
                  className="flex h-8 cursor-pointer items-center gap-2 rounded bg-teal-900 px-2 text-sm text-white hover:bg-teal-800"
                  onMouseEnter={() => setOpenRoleSlug(slug)}
                  onMouseLeave={() => setOpenRoleSlug(null)}
                >
                  <Popover
                    isOpen={openRoleSlug === slug}
                    onOpenChange={(open) => setOpenRoleSlug(open ? slug : null)}
                    position={PopoverPosition.CENTER}
                    styles={{ width: "max-content", minWidth: "160px" }}
                    trigger={
                      <button type="button" className="flex h-full cursor-pointer items-center gap-2">
                        {role?.name ?? `Unknown: ${slug}`}
                        <span className="flex items-center gap-1 text-white">
                          <FaUserGroup className="size-3" />
                          {memberNames.length}
                        </span>
                      </button>
                    }
                  >
                    <div className="body-sm px-2 py-2 text-grey-600">
                      {memberNames.length} {memberNames.length === 1 ? "Member" : "Members"}
                    </div>
                    {memberNames.length === 0 ? (
                      <div className="w-full px-4 py-3 text-center text-sm text-grey-600">No members have this role</div>
                    ) : (
                      memberNames.map((name, idx) => (
                        <div key={idx} className="flex w-full items-center gap-3 rounded-sm px-2 py-2 text-sm transition-colors hover:bg-orange-50">
                          {name}
                        </div>
                      ))
                    )}
                  </Popover>
                  <IoClose
                    className="cursor-pointer text-white"
                    onClick={() =>
                      set(
                        "roleSlugs",
                        s.roleSlugs.filter((x) => x !== slug)
                      )
                    }
                  />
                </span>
              );
            })}
            <Select
              size={SelectSize.MEDIUM}
              isSearchable
              hideClear
              placeholder="Search"
              selectedValues={[]}
              options={availableRoleOptions}
              styles={{ popover: { width: "max-content", minWidth: "160px" } }}
              onChange={(vals) => {
                if (vals[0] && !s.roleSlugs.includes(vals[0])) set("roleSlugs", [...s.roleSlugs, vals[0]]);
              }}
              customTrigger={() => (
                <Button
                  variant={ButtonVariant.GHOST}
                  className="group rounded-sm border border-grey-200 hover:border-orange-500 aria-expanded:border-orange-500"
                >
                  <span className="text-grey-900 group-hover:text-orange-500 group-aria-[expanded=true]:text-orange-500">Add Role</span>
                </Button>
              )}
            />
          </div>
          {s.roleSlugs.length === 0 && <span className="text-sm text-grey-600">No roles assigned. No members will be in this group.</span>}
        </div>
      </FlyoutField>

      <div className="border-t border-grey-300" />

      <FlyoutSection title="Trigger">
        <FlyoutField
          label="When a request comes in"
          tooltip="Choose whether to just file a ticket the team can pick up later, or also try to reach someone live."
        >
          <SingleSelect
            value={s.defaultTicketAction}
            onChange={(v) => set("defaultTicketAction", v as RoutingGroupTicketAction)}
            options={[
              { label: "Just create a ticket", value: "ticket-only" },
              { label: "Create a ticket and try to reach someone", value: "ticket-and-human" }
            ]}
          />
        </FlyoutField>
      </FlyoutSection>

      <div className="border-t border-grey-300" />

      <FlyoutSection title="Notification & Ownership">
        {s.defaultTicketAction === "ticket-and-human" && (
          <>
            <FlyoutField
              label="How to reach the team"
              tooltip="In-app rings team members in their browser. Forward sends voice callers to a phone number."
            >
              <SingleSelect
                value={s.escalationMode}
                onChange={(v) => set("escalationMode", v as RoutingGroupEscalationMode)}
                options={[
                  { label: "Ring members in-app", value: "call-center" },
                  { label: "Forward calls to a phone number", value: "call-forward" }
                ]}
              />
            </FlyoutField>
            {s.escalationMode === "call-center" && (
              <>
                <FlyoutField
                  label="Who gets the ring"
                  tooltip="One person funnels each escalation to a single owner (the default). Everyone in the group gives a faster pickup but rings more people."
                >
                  <SingleSelect
                    value={s.escalationCallCenterDispatch ?? "ring-one"}
                    onChange={(v) => set("escalationCallCenterDispatch", v as RoutingGroupEscalationDispatch)}
                    options={[
                      { label: "Just one person", value: "ring-one" },
                      { label: "Everyone in the group", value: "ring-everyone" }
                    ]}
                  />
                </FlyoutField>
                {(s.escalationCallCenterDispatch ?? "ring-one") === "ring-one" && (
                  <FlyoutField
                    label="How to pick the person"
                    tooltip="Round-robin takes turns. Least busy goes to whoever has the fewest open items right now."
                  >
                    <SingleSelect
                      value={s.escalationCallCenterPick ?? "round-robin"}
                      onChange={(v) => set("escalationCallCenterPick", v as RoutingGroupPickStrategy)}
                      options={[
                        { label: "Round-robin (take turns)", value: "round-robin" },
                        { label: "Least busy", value: "least-busy" }
                      ]}
                    />
                  </FlyoutField>
                )}
                <FlyoutField
                  label="Ring timeout (seconds)"
                  tooltip="How long to wait for one person to accept before timing out and trying the next. Leave blank to use the org default."
                >
                  <Input
                    type="number"
                    min={3}
                    max={120}
                    placeholder="org default"
                    value={s.escalationRingTimeoutSeconds ?? ""}
                    onChange={(e) => set("escalationRingTimeoutSeconds", e.target.value === "" ? null : parseInt(e.target.value, 10))}
                  />
                </FlyoutField>
                <FlyoutField
                  label="Max attempts"
                  tooltip="How many people to try before falling through to ticket-only. Leave blank to use the org default."
                >
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    placeholder="org default"
                    value={s.escalationMaxAttempts ?? ""}
                    onChange={(e) => set("escalationMaxAttempts", e.target.value === "" ? null : parseInt(e.target.value, 10))}
                  />
                </FlyoutField>
              </>
            )}
            {s.escalationMode === "call-forward" && (
              <FlyoutField label="Forward to phone number">
                <ForwardPhoneField
                  value={s.escalationForwardPhoneNumber}
                  onChange={(v) => set("escalationForwardPhoneNumber", v)}
                  error={forwardError}
                />
              </FlyoutField>
            )}
          </>
        )}

        <FlyoutField
          label={s.defaultTicketAction === "ticket-and-human" ? "If no one picks up the live escalation" : "Who handles the ticket"}
          tooltip={
            s.defaultTicketAction === "ticket-and-human"
              ? "When the live attempt fails (no one accepts, all timed out, or no eligible members), this decides who handles the ticket. If a live agent DOES pick up, the ticket goes to them and this is bypassed."
              : "Open to the group lets anyone in the group claim it. Assign to one funnels every ticket to a single owner."
          }
        >
          <SingleSelect
            value={s.ticketAssignment}
            onChange={(v) => set("ticketAssignment", v as RoutingGroupTicketAssignment)}
            options={[
              { label: "Open to the whole group", value: "open-to-group" },
              { label: "Assign to one person", value: "assign-to-one" }
            ]}
          />
        </FlyoutField>
        {s.ticketAssignment === "assign-to-one" && (
          <FlyoutField
            label="How to pick the person"
            tooltip="Round-robin takes turns. Least busy goes to whoever has the fewest open items right now."
          >
            <SingleSelect
              value={s.ticketAssignmentPick ?? "round-robin"}
              onChange={(v) => set("ticketAssignmentPick", v as RoutingGroupPickStrategy)}
              options={[
                { label: "Round-robin (take turns)", value: "round-robin" },
                { label: "Least busy", value: "least-busy" }
              ]}
            />
          </FlyoutField>
        )}
      </FlyoutSection>

      {groupSlug !== "emergency" && (
        <>
          <div className="border-t border-grey-300" />

          <FlyoutSection title="Before the Bot Escalates">
            {/* ENG-3390 — "Before connecting a caller to the team" is no longer per group: the
                bot cannot know which group a request belongs to until it has already decided
                whether to connect. It lives at the top of this page. */}
            <p className="text-sm text-grey-600">
              Whether the bot offers to help once before connecting a caller is set for every group at the top of this page.
            </p>
            <FlyoutField
              label="Before the bot files a ticket, it must have"
              tooltip="What the bot must know about the caller before it can send a request to this team. Callers on the phone or by text already give us a phone number, so 'a way to reach the caller' is only asked for on web chat. Emergencies never wait on this."
            >
              <SingleSelect
                value={s.requiredContactInfo}
                onChange={(v) => set("requiredContactInfo", v as RoutingGroupRequiredContactInfo)}
                options={REQUIRED_CONTACT_OPTIONS}
              />
            </FlyoutField>
            {s.requiredContactInfo !== "none" && (
              <FlyoutField
                label="If the caller declines to share it"
                tooltip="File anyway notes the refusal on the ticket. Callback request only files a ticket without trying to reach anyone live. Do not file means the bot explains it can't pass the request along and offers other options instead."
              >
                <SingleSelect
                  value={s.contactRefusalBehavior}
                  onChange={(v) => set("contactRefusalBehavior", v as RoutingGroupContactRefusalBehavior)}
                  options={CONTACT_REFUSAL_OPTIONS}
                />
              </FlyoutField>
            )}
          </FlyoutSection>
        </>
      )}

      <div className="border-t border-grey-300" />

      <FlyoutSection title="After Hours">
        <FlyoutField
          label="When the team is closed"
          tooltip="Hold keeps the ticket waiting until the team's next open window. Forward sends voice calls to the phone number above. Decline tells the customer the team is closed and offers self-serve — no ticket is created."
        >
          <SingleSelect
            value={s.outsideHoursMode}
            onChange={(v) => set("outsideHoursMode", v as RoutingGroupOutsideHoursMode)}
            options={[
              { label: "Hold for the team", value: "queue" },
              { label: "Forward calls to a number", value: "forward" },
              { label: "Decline politely", value: "reject" }
            ]}
          />
        </FlyoutField>
        {s.outsideHoursMode === "forward" && (
          <div className="flex flex-col gap-1">
            <FlyoutField
              label="Forward number (outside hours)"
              tooltip="Used when the team is closed. Leave blank to fall back to the in-hours forward number. If both are blank, the group's misconfigured — calls will degrade to a queued ticket and we'll mark the ticket with a [Config error] tag so operators see it."
            >
              <ForwardPhoneField
                value={s.outsideHoursForwardPhoneNumber}
                onChange={(v) => set("outsideHoursForwardPhoneNumber", v)}
                error={outsideHoursError}
              />
            </FlyoutField>
            {s.outsideHoursForwardPhoneNumber.trim() === "" && s.escalationForwardPhoneNumber.trim() === "" && (
              <p className="text-sm text-yellow-900">
                ⚠️ No forward number is set on either field. After-hours voice callers will land in a queued ticket tagged with{" "}
                <code className="rounded bg-yellow-50 px-1 font-mono text-xs">routing-group-misconfig</code> instead of being forwarded.
              </p>
            )}
          </div>
        )}
        <div className="flex flex-col gap-1">
          <FlyoutField
            label="Hours"
            tooltip="Pick which schedule decides whether this group is open. Edit the schedule itself on the Hours page — this card only chooses which one applies."
          >
            <SingleSelect
              value={s.scheduleSlug ?? DEFAULT_GROUP_SCHEDULE_SLUG}
              onChange={(v) => set("scheduleSlug", v === DEFAULT_GROUP_SCHEDULE_SLUG ? null : v)}
              options={[
                { label: `${DEFAULT_GROUP_SCHEDULE_LABEL} (default)`, value: DEFAULT_GROUP_SCHEDULE_SLUG },
                ...slugOptions.filter((o) => o.slug !== DEFAULT_GROUP_SCHEDULE_SLUG).map((o) => ({ label: o.label, value: o.slug }))
              ]}
            />
          </FlyoutField>
          <p className="text-sm text-grey-600">
            {resolvedSchedule == null
              ? "No schedule configured at this scope yet — the team will be treated as always open. Author it on the Hours page first."
              : summarizeWeeklyPeriods(resolvedSchedule.weekly)}
          </p>
        </div>
      </FlyoutSection>

      <div className="border-t border-grey-300" />
      <ExternalReceiptsSection value={s.alertEmailRecipients} onChange={(v) => set("alertEmailRecipients", v)} />
    </div>
  );
}

// Collapsed by default — same chevron/header style as the rest of the form.
function ExternalReceiptsSection(props: { value: string[]; onChange: (next: string[]) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex flex-col gap-3">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex cursor-pointer items-center gap-2">
        <LuChevronDown className={filterClasses("size-4 text-grey-600 transition-transform", !open && "-rotate-90")} />
        <p className="text-sm font-semibold text-grey-900">Advanced: External Receipts</p>
      </button>
      {open && <ExternalReceiptsFields value={props.value} onChange={props.onChange} />}
    </div>
  );
}

// Facility/DM/custom checkboxes for the external ticket-receipt email.
function ExternalReceiptsFields(props: { value: string[]; onChange: (next: string[]) => void }) {
  const orgId = Member.orgId!;
  const page = usePropertiesGetLocationIds();
  const selection = splitExternalReceipts(props.value);
  const setSelection = (patch: Partial<typeof selection>): void => props.onChange(joinExternalReceipts({ ...selection, ...patch }));

  // Live values shown once checked — resolved from Facility Info, not stored here.
  const [resolved, setResolved] = useState<{ facility?: string; dm?: string }>({});
  useEffect(() => {
    let cancelled = false;
    API.LocationVars.getVars(
      [LocationVarKey.CortexFacilityEmail, LocationVarKey.CortexFacilityDMEmail],
      orgId,
      page.groupId ?? undefined,
      page.locationId ?? undefined
    ).then((vars) => {
      if (!cancelled) setResolved({ facility: vars[LocationVarKey.CortexFacilityEmail], dm: vars[LocationVarKey.CortexFacilityDMEmail] });
    });
    return () => {
      cancelled = true;
    };
  }, [orgId, page.groupId, page.locationId]);

  return (
    <div className="flex flex-col gap-3 pt-1">
      <p className="text-sm text-grey-600">Email a receipt to the facility or DM when a ticket is created in this group.</p>
      <div className="flex items-center gap-2">
        <Checkbox
          label="Facility Email"
          name="alert-facility-email"
          checked={selection.facility}
          onCheckedChange={(v) => setSelection({ facility: !!v })}
        />
        {selection.facility && <span className="text-sm text-grey-500">{resolved.facility || "not set on Facility Info"}</span>}
      </div>
      <div className="flex items-center gap-2">
        <Checkbox label="DM Email" name="alert-dm-email" checked={selection.dm} onCheckedChange={(v) => setSelection({ dm: !!v })} />
        {selection.dm && <span className="text-sm text-grey-500">{resolved.dm || "not set on Facility Info"}</span>}
      </div>
      <RecipientListEditor
        label="Custom recipients"
        entries={selection.custom}
        onChange={(custom) => setSelection({ custom })}
        placeholder="ops@example.com"
        validate={validateEmail}
      />
    </div>
  );
}

// ─── Edit Group flyout ─────────────────────────────────────────────────────

interface GroupEditPanelProps {
  group: RoutingGroupInterface;
  inheritance: LocationVarInheritance;
  roles: RoleInterfaceMap;
  topicPrimaryAtScope: Record<string, { slug: string; name: string }>;
  slugOptions: Array<{ slug: string; label: string }>;
  resolveScheduleBySlug: (slug: string) => ScheduleInterface | null;
  /** True when the group can be reverted/deleted at this scope (drives the
   * clickable revert affordance on the header pill). */
  canDeleteOrRevert: boolean;
  /** True when a broader-scope row exists to fall back to (Revert vs Delete). */
  hasBroaderScopeRow: boolean;
  onSave: (patch: Partial<RoutingGroupInterface>) => Promise<void>;
  onCancel: () => void;
  onRevert: () => Promise<void>;
}

/**
 * The "Edit Group" flyout body (rendered inside a right-side `Panel`).
 *
 * Every field — name, description, topics, roles, and the Select dropdowns — is
 * buffered in local state and nothing is written until Save. The underlying
 * card therefore reflects no changes until the operator confirms.
 */
function GroupEditPanel(props: GroupEditPanelProps) {
  const { group, inheritance, roles, topicPrimaryAtScope, slugOptions, resolveScheduleBySlug, canDeleteOrRevert, hasBroaderScopeRow } = props;
  const [settings, setSettings] = useState<GroupSettings>(() => groupToSettings(group));
  const forwardError = validatePhone(settings.escalationForwardPhoneNumber);
  const outsideHoursError = validatePhone(settings.outsideHoursForwardPhoneNumber);
  const handleSave = (): Promise<void> => {
    if (forwardError || outsideHoursError) return Promise.resolve();
    return props.onSave(settingsToBody(settings));
  };

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex h-[50px] shrink-0 items-center gap-2 border-b border-grey-200 px-4">
        <p className="text-base font-semibold text-grey-900">Edit {group.name} Group</p>
        {/* Read-only status pill. The revert/delete action lives in the footer. */}
        <InheritancePill
          inheritance={inheritance}
          isEditing={false}
          canRevert={false}
          alwaysShow
          customText={{ custom: "Customized" }}
          onClick={() => {}}
        />
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        <GroupSettingsForm
          value={settings}
          onChange={setSettings}
          roles={roles}
          topicPrimaryAtScope={topicPrimaryAtScope}
          slugOptions={slugOptions}
          resolveScheduleBySlug={resolveScheduleBySlug}
          groupSlug={group.slug}
          groupName={group.name}
        />
      </div>

      <div className="flex h-[50px] shrink-0 items-center justify-between gap-2 border-t border-grey-200 bg-white px-4">
        {canDeleteOrRevert ? (
          <Button variant={ButtonVariant.GHOST} className="-ml-3" onClickAsync={props.onRevert}>
            {hasBroaderScopeRow ? "Revert to Default" : "Delete group"}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <Button color={ButtonColor.WHITE} onClick={props.onCancel}>
            Cancel
          </Button>
          <Button color={ButtonColor.ORANGE} onClickAsync={handleSave} disabled={!!forwardError || !!outsideHoursError}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function FlyoutSection(props: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-semibold text-grey-900">{props.title}</p>
      {props.children}
    </div>
  );
}

function SingleSelect(props: { value: string; options: Array<{ label: string; value: string }>; onChange: (value: string) => void }) {
  return (
    <Select
      size={SelectSize.MEDIUM}
      hideClear
      selectedValues={[props.value]}
      options={props.options}
      onChange={(vals) => {
        if (vals[0] !== undefined) props.onChange(vals[0]);
      }}
    />
  );
}

// ─── Create-group modal ───────────────────────────────────────────────────

/**
 * Full-form "Create Group" modal. Renders its own animated frame (bare mode)
 * and uses the same `GroupSettingsForm` as the Edit flyout so the two surfaces
 * stay identical. Submitting hands the built body up for a single upsert.
 */
function CreateGroupModal(props: {
  existingGroups: Array<{ slug: string; name: string; isCustom: boolean }>;
  topicPrimaryAtScope: Record<string, { slug: string; name: string }>;
  roles: RoleInterfaceMap;
  slugOptions: Array<{ slug: string; label: string }>;
  resolveScheduleBySlug: (slug: string) => ScheduleInterface | null;
  onCreate: (body: Partial<RoutingGroupInterface>) => Promise<void>;
}) {
  const { topicPrimaryAtScope, roles, slugOptions, resolveScheduleBySlug } = props;
  const [settings, setSettings] = useState<GroupSettings>(blankGroupSettings);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Drive open/close locally so the exit animation can play before the modal
  // system unmounts us. `beginClose` triggers the exit; the overlay's
  // onAnimationComplete then actually tears the modal down.
  const [isOpen, setIsOpen] = useState(true);
  const beginClose = () => setIsOpen(false);
  // Scroll shadows: the header gets one once scrolled away from the top, the
  // footer gets one while there's still content below (not yet at the bottom).
  const bodyRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  const updateScrollShadows = () => {
    const el = bodyRef.current;
    if (!el) return;
    setScrolled(el.scrollTop > 0);
    setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
  };
  // Re-measure when content height changes (fields show/hide with settings).
  useEffect(updateScrollShadows, [settings]);

  const trimmedName = settings.name.trim();
  const derivedSlug = trimmedName ? slugifyGroupName(trimmedName) : "";

  // Pre-flight collision check against the active scope. Same-slug rejects
  // even if the existing group is inherited — operator should "Customize"
  // the existing card instead of creating a new one that would silently
  // shadow the inherited row.
  const collision = trimmedName ? props.existingGroups.find((g) => g.slug === derivedSlug) : null;
  const collisionMessage = collision
    ? collision.isCustom
      ? `"${collision.name}" already exists at this scope. Pick a different name, or edit the existing group's card.`
      : `"${collision.name}" already applies here (inherited). Click "Customize" on its card instead of creating a new one — Create here would shadow the inherited settings.`
    : null;

  const forwardError = validatePhone(settings.escalationForwardPhoneNumber);
  const outsideHoursError = validatePhone(settings.outsideHoursForwardPhoneNumber);

  const submit = async () => {
    if (!trimmedName || collision || forwardError || outsideHoursError) return;
    setLoading(true);
    setSubmitError(null);
    try {
      await props.onCreate(settingsToBody(settings));
      beginClose();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && beginClose()}>
      <Dialog.Portal>
        <Dialog.Overlay asChild>
          <motion.div
            className="fixed inset-0 z-500 flex items-center justify-center bg-[rgba(0,0,0,0.24)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: isOpen ? 1 : 0 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            onAnimationComplete={() => {
              if (!isOpen) ModalModel.hideModal();
            }}
          >
            <Dialog.Content asChild onEscapeKeyDown={beginClose}>
              <motion.div
                className="flex max-h-[85vh] w-[600px] flex-col overflow-hidden rounded bg-white shadow-[0px_6px_16px_0px_rgba(0,0,0,0.12)]"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: isOpen ? 1 : 0, scale: isOpen ? 1 : 0.97 }}
                transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
              >
                <div
                  className={filterClasses(
                    "relative z-10 flex shrink-0 items-center justify-between p-4 transition-shadow",
                    scrolled && "shadow-[0px_2px_4px_0px_rgba(0,0,0,0.08)]"
                  )}
                >
                  <Dialog.Title className="text-base font-semibold text-grey-900">Create Group</Dialog.Title>
                  <button type="button" aria-label="Close" onClick={beginClose} className="cursor-pointer text-grey-900 hover:text-orange-500">
                    <IoClose className="size-4" />
                  </button>
                </div>

                <div ref={bodyRef} className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4" onScroll={updateScrollShadows}>
                  <GroupSettingsForm
                    value={settings}
                    onChange={setSettings}
                    roles={roles}
                    topicPrimaryAtScope={topicPrimaryAtScope}
                    slugOptions={slugOptions}
                    resolveScheduleBySlug={resolveScheduleBySlug}
                  />
                  {collisionMessage && <p className="text-sm text-red-500">{collisionMessage}</p>}
                  {submitError && <p className="text-sm text-red-500">{submitError}</p>}
                </div>

                <div
                  className={filterClasses(
                    "relative z-10 flex shrink-0 items-center justify-end gap-2 p-4 transition-shadow",
                    !atBottom && "border-t border-grey-200 shadow-[0px_-2px_4px_0px_rgba(0,0,0,0.08)]"
                  )}
                >
                  <Button variant={ButtonVariant.GHOST} loading={loading} onClick={beginClose}>
                    Cancel
                  </Button>
                  <Button
                    color={ButtonColor.ORANGE}
                    loading={loading}
                    onClickAsync={submit}
                    disabled={!trimmedName || !!collision || !!forwardError || !!outsideHoursError}
                  >
                    Create Group
                  </Button>
                </div>
              </motion.div>
            </Dialog.Content>
          </motion.div>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
